/**
 * Messenger GRAMMAR + date helpers for the agent IM tabs.
 *
 * The Telegram/WhatsApp tabs must LOOK AND FEEL like the real web clients
 * (operator direction 2026-07-22/23). Colour alone does not achieve that: the
 * two clients differ STRUCTURALLY — most of all in where the bubble tail sits
 * (Telegram: bottom-outer corner of the LAST bubble of a sender-run; WhatsApp:
 * top-outer corner of the FIRST) — so this module carries a per-channel
 * *grammar* record, not just a palette, and both tabs render from ONE component
 * with two configurations. There is deliberately no forked `WhatsAppThread`.
 *
 * ## Where the WhatsApp numbers come from
 * Measured 2026-07-23 off the operator's live `web.whatsapp.com` (2025 "WDS"
 * redesign build) by reading CSS custom properties and computed geometry —
 * never from remembered pixels, and never by reading conversation content. The
 * redesign moved several values that older references still quote:
 *
 *   wallpaper  #F5F1EB (doodle #EAE0D3)   dark #161717 (doodle white/10)
 *   incoming   #FFFFFF                    dark #242626   (NOT #202c33)
 *   outgoing   #D9FDD3                    dark #144D37   (NOT #005c4b)
 *   bubble meta rgba(0,0,0,.6)            dark rgba(255,255,255,.6)
 *   day/system pill rgba(255,255,255,.9)  dark #1D1F1F
 *   E2E-notice pill #FFF0D4               dark #1D1F1F
 *   header/tray #F7F5F3                   dark #161717
 *   composer surface #FFFFFF              dark #242626
 *   divider    rgba(0,0,0,.1)             dark rgba(255,255,255,.1)
 *   bubble max-width 65% · list row 76px · list avatar 48px · NO row dividers
 *
 * Geometry retune measured 2026-07-25 off the first open thread (a carrier
 * notification thread — no client content read, chat list never touched):
 *   bubble radius 7.5px (tailed corner SQUARED at 0px, wing is a separate SVG we
 *   don't ship) · text inset 6/7/8/9px t/r/b/l · shadow 0 1px .5px rgba(11,20,26,.13)
 *   · meta 11px rgba(0,0,0,.6) · day-pill radius 6px · composer: attach + emoji
 *   OUTSIDE the input to its left, mic outside-right.
 *
 * Telegram's values are unchanged from the shipped skin — its look is signed
 * off and must not drift.
 *
 * Every class FRAGMENT is a literal string IN THIS FILE, which is what Tailwind
 * v4's source scan needs. Fields may be concatenated from such fragments (see
 * `DOT_TEXTURE`) because the scanner still sees each piece — but a class must
 * never be assembled from values computed at runtime, which it could not see.
 */

/** Chat-list stamp grammars — see `listStamp()`. */
export type ListStampStyle = "telegram" | "whatsapp";

export interface MessengerTheme {
  // ── chat body ──────────────────────────────────────────────────────────
  /** Chat wallpaper: base tint + the channel's texture layer. */
  wallpaperClass: string;

  // ── bubbles ────────────────────────────────────────────────────────────
  /** Incoming (agent) bubble. */
  inBubbleClass: string;
  /** Outgoing (account holder) bubble — the channel's signature colour. */
  outBubbleClass: string;
  /** Radius of the three non-tail corners. */
  radiusClass: string;
  /** Bubble width cap. */
  maxWidthClass: string;
  /** Bubble elevation over the wallpaper. */
  shadowClass: string;
  /** Bubble inner padding. */
  paddingClass: string;
  /**
   * Which end of a sender-run carries the tail — the single biggest structural
   * difference between the two clients. `"bottom"` tails the LAST bubble of a
   * run (Telegram); `"top"` tails the FIRST (WhatsApp).
   */
  tailEdge: "top" | "bottom";
  /** Tail corner on the holder's (right) bubbles. */
  tailOutClass: string;
  /** Tail corner on the agent's (left) bubbles. */
  tailInClass: string;
  /** In-bubble meta (size + colour) on incoming bubbles. */
  metaInClass: string;
  /** In-bubble meta on outgoing bubbles — the clients tint it differently over
   *  the coloured bubble, so it is a separate field even where the two agree. */
  metaOutClass: string;
  /** Where the floated meta sits inside the bubble — must track `paddingClass`
   *  so the timestamp lines up with the text's own inset. */
  metaInsetClass: string;
  /** Gap above a bubble that starts a new sender-run. */
  runGapClass: string;
  /** Gap above a bubble that continues the run. */
  tightGapClass: string;

  // ── pills ──────────────────────────────────────────────────────────────
  /** The centered day separator. */
  dayPillClass: string;
  /** WhatsApp uppercases the day label ("HOJE"); Telegram does not. Applied as
   *  a CSS transform — NEVER by uppercasing the translated string, which would
   *  be wrong in locales with different casing rules. */
  dayPillUpperClass: string;
  /**
   * The system-notice slot at the top of a conversation. WhatsApp puts its
   * end-to-end-encryption notice here; this surface is a read-only MIRROR of
   * the agent's traffic, not an E2EE session, so we take the visual slot and
   * put a truthful Lexiun notice in it. Empty ⇒ the channel has no such slot.
   */
  systemPillClass: string;

  // ── chrome ─────────────────────────────────────────────────────────────
  /** Conversation header bar. */
  headerClass: string;
  /** Composer bar. */
  composerBarClass: string;
  /** The composer's input container (which carries our read-only notice). */
  composerInputClass: string;
  /** Chat-list column. */
  listPanelClass: string;
  /**
   * The two-column "pick a conversation" column. WhatsApp shows its wallpaper
   * there; Telegram keeps the neutral panel this tab shipped with — repainting
   * it Telegram-blue would be drift, not fidelity.
   */
  emptyPaneClass: string;
  /** The hairline splitting the list column from the conversation column. */
  panelBorderClass: string;
  /** Hairlines between chat-list rows — the WhatsApp redesign has none. */
  listRowDividerClass: string;
  /** Chat-list row padding (WhatsApp's rows are taller: 76px). */
  listRowClass: string;
  /**
   * The chat-list search field's container. Empty ⇒ the channel's list has no
   * search field. It filters the conversations the pane ALREADY holds — a
   * decorative search box would be a lie on a monitoring surface.
   */
  listSearchClass: string;
  /**
   * How a chat-list row stamps its last message. The clients genuinely differ,
   * so this is NOT shared: WhatsApp uses four buckets ending in a full numeric
   * date (`22/07/2026`); Telegram (verified against the live client 2026-07-23,
   * which showed `Jun 28`) keeps the two-way split this tab already shipped.
   * Giving Telegram WhatsApp's buckets would be drift, not fidelity.
   */
  listStamp: ListStampStyle;
}

/** A faint dot texture that reads subtly over both light and dark tints —
 *  Telegram's shipped wallpaper treatment. */
const DOT_TEXTURE =
  "bg-[radial-gradient(rgba(120,120,120,0.06)_0.5px,transparent_0.6px)] bg-[length:20px_20px]";

const TELEGRAM: MessengerTheme = {
  wallpaperClass: `bg-[#dfe8f1] dark:bg-[#0e1621] ${DOT_TEXTURE}`,
  inBubbleClass: "bg-white text-coffee-900 dark:bg-[#182533] dark:text-cream-100",
  outBubbleClass: "bg-[#effdde] text-coffee-900 dark:bg-[#2b5278] dark:text-cream-100",
  radiusClass: "rounded-[18px]",
  maxWidthClass: "max-w-[82%]",
  shadowClass: "shadow-sm",
  paddingClass: "px-3 py-1.5",
  tailEdge: "bottom",
  tailOutClass: "rounded-br-[5px]",
  tailInClass: "rounded-bl-[5px]",
  metaInClass: "text-[10px] text-coffee-500 dark:text-cream-500",
  metaOutClass: "text-[10px] text-coffee-500 dark:text-cream-500",
  metaInsetClass: "bottom-1.5 right-3",
  runGapClass: "mt-2",
  tightGapClass: "mt-0.5",
  dayPillClass:
    "rounded-full bg-black/10 text-coffee-700 dark:bg-black/40 dark:text-cream-200",
  dayPillUpperClass: "",
  systemPillClass: "",
  headerClass:
    "border-cream-200/60 bg-cream-50 dark:border-coffee-800/60 dark:bg-coffee-900",
  composerBarClass:
    "border-cream-200/60 bg-cream-50 dark:border-coffee-800/60 dark:bg-coffee-900",
  composerInputClass: "rounded-2xl bg-white dark:bg-coffee-800",
  listPanelClass: "",
  emptyPaneClass: "bg-cream-50/60 dark:bg-coffee-950/40",
  panelBorderClass: "border-cream-200/60 dark:border-coffee-800/60",
  listRowDividerClass: "divide-y divide-cream-200/50 dark:divide-coffee-800/50",
  listRowClass: "px-3 py-2.5",
  listSearchClass: "",
  listStamp: "telegram",
};

const WHATSAPP: MessengerTheme = {
  // Base tint + the original doodle tile (defined in index.css — a data-URI SVG
  // so the page stays self-contained and no asset is fetched).
  wallpaperClass: "messenger-wallpaper-whatsapp",
  inBubbleClass: "bg-white text-coffee-900 dark:bg-[#242626] dark:text-cream-100",
  outBubbleClass: "bg-[#d9fdd3] text-coffee-900 dark:bg-[#144d37] dark:text-cream-100",
  radiusClass: "rounded-[7.5px]",
  maxWidthClass: "max-w-[65%]",
  shadowClass: "shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] dark:shadow-none",
  // Measured 2026-07-25 off the live thread: text inset 6/7/8/9px (t/r/b/l). The
  // side asymmetry mirrors by tail side (in: 9px left, out: 9px right) so a single
  // shared class averages the horizontal to 8px; the real correction is the
  // bottom (8px, clearing the floated meta) — was 6px.
  paddingClass: "px-2 pt-1.5 pb-2",
  tailEdge: "top",
  // The tailed corner is SQUARED (measured 0px, not the 3px we first guessed):
  // WhatsApp draws the protruding wing as a separate SVG over a 0px corner. We
  // ship no wing (plan item 2, deferred), so the squared corner alone carries the
  // tail signal — and the sharp asymmetry reads more "WhatsApp" than a soft 3px.
  tailOutClass: "rounded-tr-none",
  tailInClass: "rounded-tl-none",
  // Meta measured 2026-07-25 at 11px rgba(0,0,0,.6) on BOTH bubbles (the green
  // outgoing bubble is not tinted differently — item 5's hypothesis refuted).
  metaInClass: "text-[11px] text-black/60 dark:text-white/60",
  metaOutClass: "text-[11px] text-black/60 dark:text-white/60",
  // Tracks paddingClass: the floated meta's bottom must equal the bubble's bottom
  // padding (now pb-2 = 8px) so it lines up with the last text line's reserved
  // inline spacer — bumped 1.5→2 alongside the padding retune.
  metaInsetClass: "bottom-2 right-2",
  runGapClass: "mt-3",
  tightGapClass: "mt-0.5",
  // Day pill radius measured 2026-07-25 at 6px (tighter than the 7.5px bubble —
  // the two genuinely differ). bg rgba(255,255,255,.9) confirmed = bg-white/90.
  dayPillClass:
    "rounded-[6px] bg-white/90 text-black/60 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] dark:bg-[#1d1f1f] dark:text-white/60 dark:shadow-none",
  dayPillUpperClass: "uppercase",
  // The E2E-notice slot shares the day pill's rounded-rectangle treatment (same
  // pill family); aligned to 6px for consistency — its own radius wasn't in view
  // to measure independently.
  systemPillClass:
    "rounded-[6px] bg-[#fff0d4] text-black/60 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] dark:bg-[#1d1f1f] dark:text-white/60 dark:shadow-none",
  headerClass: "border-black/10 bg-[#f7f5f3] dark:border-white/10 dark:bg-[#161717]",
  composerBarClass: "border-black/10 bg-[#f7f5f3] dark:border-white/10 dark:bg-[#161717]",
  composerInputClass: "rounded-[24px] bg-white dark:bg-[#242626]",
  listPanelClass: "bg-white dark:bg-[#161717]",
  emptyPaneClass: "messenger-wallpaper-whatsapp",
  panelBorderClass: "border-black/10 dark:border-white/10",
  // The 2025 redesign draws NO hairline between rows (measured: 76px rows, 4px
  // apart, no border) — so the list must not invent one.
  listRowDividerClass: "",
  listRowClass: "px-3 py-3.5",
  listSearchClass: "rounded-[24px] bg-[#f7f5f3] dark:bg-[#242626]",
  listStamp: "whatsapp",
};

/**
 * Fallback for any other channel id: Telegram's GRAMMAR (bottom tails, its
 * geometry and chrome) with a neutral palette — deliberately, since Telegram's
 * is the shape this surface shipped with. Note the consequence: a Telegram-only
 * grammar field added later is inherited here unless it is overridden below.
 */
const DEFAULT: MessengerTheme = {
  ...TELEGRAM,
  wallpaperClass: `bg-cream-100 dark:bg-coffee-950 ${DOT_TEXTURE}`,
  inBubbleClass: "bg-white text-coffee-900 dark:bg-coffee-800 dark:text-cream-100",
  outBubbleClass: "bg-sky-50 text-coffee-900 dark:bg-sky-950/50 dark:text-cream-100",
};

export function getMessengerTheme(channel: string): MessengerTheme {
  if (channel === "telegram") return TELEGRAM;
  if (channel === "whatsapp") return WHATSAPP;
  return DEFAULT;
}

// ── Inline markdown (bubbles render bold/italic/code, like the real clients) ──

export type InlineSeg = { type: "text" | "bold" | "italic" | "code"; value: string };

/**
 * Splits a message into inline segments — `**bold**`/`__bold__`, `*italic*`/
 * `_italic_`, and `` `code` `` — so a bubble can render the light formatting the
 * agent emits instead of showing raw asterisks. Pure + flat (no nesting, no
 * block markdown, no HTML) so the render stays a plain list of text nodes and
 * cannot inject markup. Unmatched markers stay literal text.
 */
export function parseInline(text: string): InlineSeg[] {
  const segs: InlineSeg[] = [];
  // Bold `**…**`/`__…__` and code `` `…` `` are matched plainly; single-marker
  // italic requires WORD BOUNDARIES + no inner whitespace, so intraword markers
  // in identifiers (`lx_read_media`) and arithmetic (`2 * 3`) stay literal.
  const re =
    /(\*\*|__)(?=\S)(.+?)(?<=\S)\1|(?<![A-Za-z0-9])([*_])(?=\S)(.+?)(?<=\S)\3(?![A-Za-z0-9])|`([^`]+?)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) segs.push({ type: "text", value: text.slice(last, m.index) });
    if (m[2] !== undefined) segs.push({ type: "bold", value: m[2] });
    else if (m[4] !== undefined) segs.push({ type: "italic", value: m[4] });
    else if (m[5] !== undefined) segs.push({ type: "code", value: m[5] });
    last = re.lastIndex;
  }
  if (last < text.length) segs.push({ type: "text", value: text.slice(last) });
  return segs;
}

/** The plain text of a message (markers removed) — for one-line list previews. */
export function stripInline(text: string): string {
  return parseInline(text)
    .map((s) => s.value)
    .join("");
}

/** Do two epoch-ms timestamps fall on the same calendar day (local time)? */
export function sameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** Is `ts` on the calendar day before `now`? */
function isYesterday(ts: number, now: number): boolean {
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  return sameDay(ts, y.getTime());
}

/**
 * The label a day-separator pill shows: "today"/"yesterday" (localized, passed
 * in) relative to `now`, else the formatted date (year only when it differs
 * from `now`'s year, exactly as the messenger clients do). `now` is a parameter
 * so this stays pure and testable.
 */
export function dayLabel(
  ts: number,
  now: number,
  locale: string,
  labels: { today: string; yesterday: string },
): string {
  if (sameDay(ts, now)) return labels.today;
  if (isYesterday(ts, now)) return labels.yesterday;
  const sameYear = new Date(ts).getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(ts);
}

/**
 * The stamp a CHAT-LIST row shows for its last message — per the CHANNEL's own
 * grammar, because the two clients genuinely differ:
 *
 *   whatsapp: today → time · yesterday → the word · 2-6 days → weekday ·
 *             older → full numeric date (`22/07/2026`)
 *   telegram: today → time · older → short numeric date (`22/07`)
 *
 * Telegram's is the two-way split this tab already shipped, kept deliberately:
 * the live client shows `Jun 28` for an old chat, so its real grammar is a
 * third thing again, and inventing WhatsApp's buckets for it would be drift
 * rather than fidelity. (Matching Telegram's actual month-day form is a
 * separate, unapproved improvement — recorded in the WA-2 notes.)
 *
 * Pure (takes `now`) so the bucket boundaries are unit-testable.
 */
export function listStamp(
  ts: number,
  now: number,
  locale: string,
  labels: { yesterday: string },
  style: ListStampStyle,
): string {
  if (sameDay(ts, now)) {
    return new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(ts);
  }
  if (style === "telegram") {
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(ts);
  }
  if (isYesterday(ts, now)) return labels.yesterday;
  // "Within the last week" is measured in whole calendar days so the bucket
  // flips at midnight like the client does, not at a rolling 168h mark.
  const startOfDay = (t: number) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const daysAgo = Math.round((startOfDay(now) - startOfDay(ts)) / 86_400_000);
  if (daysAgo >= 2 && daysAgo <= 6) {
    return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(ts);
  }
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(ts);
}
